import { ChatService } from "../service/chat_service";
import { __vueEvent } from "../constant";
import { EVENTS } from "../enums";
import { getQueryStringValue } from "./get_querystring_value";

/**
 * SD-13570 Step 5 (chat-page half) — the transport-agnostic token intake.
 *
 * The host is the window that embeds us — window.parent — because both
 * supported integrations render the chat in an iframe: the embedder and the
 * standalone iframe drop-in. A separately opened chat window is out of scope,
 * so no other window may hand us a credential.
 *
 * Works at ANY point in a conversation, not just at open: the mid-chat
 * anonymous→authenticated upgrade is just this listener firing later.
 *
 * The token is an opaque credential; trust comes from the server-side RS256
 * verification, never from this message. A hostile page posting garbage gets
 * a rejection; posting a *valid* token merely authenticates as themselves.
 */

const AUTH_TOKEN_MESSAGE = "satisfi.auth.token";
const AUTH_REQUEST_MESSAGE = "satisfi.auth.request";
const AUTH_CLEAR_MESSAGE = "satisfi.auth.clear";

let authenticated = false;

/**
 * The server-issued session key. Held here and sent in the body of subsequent
 * requests: the chat page runs in an iframe, so the intake's cookie is
 * a third-party cookie that browsers may refuse outright — relying on it meant
 * every message after login silently travelled anonymous.
 *
 * Opaque and server-issued. It names a session; it never asserts an identity,
 * so possession is all it proves and the server still resolves who that is.
 */
let sessionKey = "";

/**
 * SD-13570 D6b — the client site's login is the source of truth for whether
 * this visitor is signed in; we keep no clock of our own. What we keep is the
 * moment the site last vouched for them, so we know when that statement has
 * gone stale and we should go back and ask again.
 */
let lastVerifiedAt = 0;

/**
 * False when the host was asked for a fresh token and could not produce one.
 * The session is not destroyed — a slow or briefly unavailable host is not
 * proof of a sign-out — but while this is false we stop presenting the session
 * key, so answers go back to anonymous until the site vouches again.
 */
let confirmed = false;

/**
 * How long the site's last confirmation stays good before we go back and ask
 * again. Overridden per environment by AuthChat:FreshnessWindowMinutes, which
 * arrives on the page config; this value is the fallback when the server sends
 * nothing, so the behaviour is the same either way.
 */
export let FRESHNESS_WINDOW_MS = 30 * 60 * 1000;

/**
 * How long we wait for a host to answer a refresh before treating it as a no.
 * Overridden by AuthChat:HostRefreshTimeoutSeconds.
 */
export let REFRESH_TIMEOUT_MS = 30 * 1000;

/**
 * Apply the server's timings. Called from the config loader once the page
 * config is known, before anything asks the host for a token.
 *
 * Both values are ignored unless positive: a missing or malformed setting must
 * leave the defaults standing rather than collapse the window to zero, which
 * would make every single message re-ask the host.
 */
export function configureAuthSession(config: {
	authFreshnessWindowSeconds?: number;
	authRefreshTimeoutSeconds?: number;
}): void {
	const freshness = Number(config?.authFreshnessWindowSeconds);
	if (Number.isFinite(freshness) && freshness > 0) {
		FRESHNESS_WINDOW_MS = freshness * 1000;
	}
	const timeout = Number(config?.authRefreshTimeoutSeconds);
	if (Number.isFinite(timeout) && timeout > 0) {
		REFRESH_TIMEOUT_MS = timeout * 1000;
	}
}

/** Resolver for an in-flight refresh, so concurrent callers share one ask. */
let pendingRefresh: Promise<boolean> | null = null;
let settleRefresh: ((ok: boolean) => void) | null = null;

/**
 * The page this chat is. Read from the query string rather than the Vuex store
 * because this module runs before the store has it: initAuthSession() is called
 * at index.ts module scope, while SetPageId is committed inside
 * ConfigLoader.load(). An eager host can post a token in that gap, and the
 * exchange needs the page id to be right at that moment. The store's pageId is
 * itself read from this same query parameter, so this is the source, not a
 * workaround.
 */
function chatPageId(): string {
	return getQueryStringValue("pageID") || "";
}

/**
 * The window that embeds this page — the only party allowed to hand us a
 * credential. Both supported transports are iframes, so that is always
 * window.parent; a top-level document has no host and uses the native entry
 * point instead.
 */
function hostWindow(): Window | null {
	if (window.parent && window.parent !== window) return window.parent;
	return null;
}

/**
 * The one intake. Every transport funnels here and nothing downstream knows
 * or cares which one delivered the credential — that is the Part-4 constraint
 * from Randy's Phase 1 doc, and it is what lets Phase 2 (app webview) reuse
 * this with zero server changes.
 *
 * Returns whether the session is now authenticated, so a native caller can
 * act on the outcome instead of guessing.
 */
async function consumeToken(token: string): Promise<boolean> {
	try {
		const result = await new ChatService().authenticateSession(chatPageId(), token, sessionKey);
		const ok = !!result?.authenticated;
		if (!ok) {
			// A rejected token must not downgrade a session that is already
			// working — a host that re-sends a stale credential would
			// otherwise silently sign the visitor out mid-conversation.
			settleRefresh?.(false);
			return false;
		}
		// Is this a NEW session, or the same one re-confirmed? Under D6b a
		// re-confirmation is routine — every few minutes on an active chat —
		// and it must stay invisible. Announcing one as a new session makes the
		// socket re-join and the transcript re-fetch, which appends the message
		// the visitor just sent a second time.
		const isNewSession = !authenticated || result.sessionKey !== sessionKey;

		authenticated = true;
		sessionKey = result.sessionKey || "";
		lastVerifiedAt = Date.now();
		confirmed = true;
		settleRefresh?.(true);

		// A different person has signed in on a browser still holding the previous
		// one's conversation. Randy's Part 2 rule: they get a new conversation,
		// never a re-assignment. Dropping the stored id and reloading is the whole
		// implementation — the page comes back, asks for a conversation, and is
		// issued a fresh one.
		//
		// The id is removed BEFORE the reload, so the reload cannot ask the same
		// question again and loop: with nothing stored there is no previous owner
		// left to mismatch against.
		if (result.conversationReset) {
			try {
				localStorage.removeItem("conv_id" + chatPageId());
			} catch { /* storage unavailable — the reload still starts clean */ }
			window.location.reload();
			return true;
		}

		// A page reload loses this module's state, so the socket joins and
		// the history fetch both run anonymously while the token is still in
		// flight — and a stamped conversation refuses both. Announcing the
		// session lets them retry now that we can prove who we are.
		//
		// Only for a session that is actually new. A refresh of the session we
		// already hold changes nothing the socket or the transcript care about:
		// same key, same identity, same conversation.
		if (isNewSession) {
			__vueEvent.emit(EVENTS.AuthSessionEstablished);
		}
		return true;
	} catch {
		// A failed exchange leaves an anonymous session anonymous, and
		// leaves a working one working. Signing someone out because a
		// network call failed is a worse outcome than ignoring the attempt;
		// only an explicit clear drops a live session.
		settleRefresh?.(false);
		return false;
	}
}

/**
 * SD-13570 D6b — ask the host whether this visitor is still signed in, the
 * only way we have of asking: request a fresh token and see whether the site
 * will mint one. A site that mints is still logged in; a site that will not
 * is not.
 *
 * Concurrent callers share one ask — a burst of messages must not turn into a
 * burst of postMessages at the host page.
 */
function refreshFromHost(): Promise<boolean> {
	if (pendingRefresh) return pendingRefresh;

	// Built into a local first. `finish` can run synchronously (the no-host
	// branch below), and a synchronous finish clearing `pendingRefresh` before
	// the assignment lands would leave the resolved promise stuck there — every
	// later call would then return it and never ask the host again.
	const refresh = new Promise<boolean>((resolve) => {
		let done = false;
		const finish = (ok: boolean) => {
			if (done) return;
			done = true;
			settleRefresh = null;
			// cleared after the fact, so a synchronous finish cannot race the
			// assignment below
			Promise.resolve().then(() => { pendingRefresh = null; });
			clearTimeout(timer);
			resolve(ok);
		};
		// Resolved by consumeToken when the host's answer completes its
		// round trip, so we wait for a VERIFIED token and not merely for
		// one to arrive.
		settleRefresh = finish;
		const timer = setTimeout(() => finish(false), REFRESH_TIMEOUT_MS);

		const host = hostWindow();
		if (!host) {
			// No host to ask. A native webview host pushes tokens in on its
			// own schedule via satisfiAuth.setToken, so there is nothing to
			// request here and no answer to wait for.
			finish(false);
			return;
		}
		host.postMessage({ type: AUTH_REQUEST_MESSAGE }, "*");
	});

	pendingRefresh = refresh;
	return refresh;
}

/**
 * SD-13570 D6b — call before anything that would answer personally.
 *
 * Cheap by design: while the site's last confirmation is inside the freshness
 * window we do nothing at all, so a visitor chatting continuously never pays a
 * round trip. Only once that statement has aged out do we go back to the host,
 * which bounds how stale our view of their login can be without putting the
 * host page in the path of every single message.
 */
export async function ensureFreshSession(): Promise<void> {
	if (!authenticated) return;
	if (confirmed && Date.now() - lastVerifiedAt <= FRESHNESS_WINDOW_MS) return;

	const ok = await refreshFromHost();
	// A host that will not mint means the site no longer vouches for this
	// visitor, so personal answers stop. The session is not torn down: the
	// next successful mint restores it silently, and a real sign-out arrives
	// as an explicit clear.
	confirmed = ok;
}

/**
 * Drop the identity locally and revoke it server-side. Local state goes first
 * and unconditionally: this tab must stop acting authenticated even if the
 * revoke call fails, and an orphaned server row expires on its own.
 */
async function clearSessionLocally(): Promise<void> {
	const revoking = sessionKey;
	authenticated = false;
	sessionKey = "";
	lastVerifiedAt = 0;
	confirmed = false;
	__vueEvent.emit(EVENTS.AuthSessionCleared);
	if (revoking) {
		try {
			await new ChatService().clearSession(revoking);
		} catch {
			// best effort — the local session is already gone
		}
	}
}

export function initAuthSession(): void {
	window.addEventListener("message", async (event: MessageEvent) => {
		const data = event.data;
		if (!data) return;

		const isToken = data.type === AUTH_TOKEN_MESSAGE && typeof data.token === "string";
		const isClear = data.type === AUTH_CLEAR_MESSAGE;
		if (!isToken && !isClear) return;

		// The sender must be the window that embeds us. Note what this does
		// and does not buy: it
		// says WHICH WINDOW spoke, never which site, and a stolen token can be
		// redeemed by curl or from the console of the client's own page without
		// touching this path at all. The credential's authority is the server's
		// RS256 verification — this guard only keeps unrelated windows out.
		// Transports with no host window use the native entry point below.
		const host = hostWindow();
		if (!host || event.source !== host) return;

		if (isClear) {
			await clearSessionLocally();
			return;
		}

		await consumeToken(data.token);
	});

	// Native entry point for transports with no host window: an app webview
	// loading this page top-level is not framed, so window.parent is the window
	// itself and the message guard above can never admit anything. The native
	// side calls this directly (evaluateJavaScript / evaluateJavascript).
	//
	// This is the SAME intake the postMessage path uses — the Part-4 rule is
	// that nothing branches on transport, so there is one consumeToken and one
	// server contract behind both.
	//
	// This is not a weaker path than postMessage. What authorises a credential
	// is the server's RS256 verification against the company's registered key,
	// plus the page's own eligibility flag — neither of which this page can
	// influence. A caller here can only sign the visitor in as whoever it
	// already holds a valid token for.
	(window as any).satisfiAuth = {
		setToken: (token: string) =>
			typeof token === "string" && token ? consumeToken(token) : Promise.resolve(false),
		clearToken: () => clearSessionLocally()
	};
}

/**
 * Ask the host for a token. Called once the page config is known, because it
 * is gated on the page being eligible — an ineligible page must never ask a
 * host for credentials.
 *
 * Deliberately separate from initAuthSession(): the listener registers as
 * early as possible so an eager host that pushes a token before config
 * arrives is not missed, while the request waits until eligibility is known.
 *
 * Covers page reloads (the host re-supplies) and hosts whose user logged in
 * before the chat page finished loading. Carries no data, so the broadcast
 * targetOrigin is safe; hosts with no token simply do not answer.
 */
export function requestHostTokenIfEligible(authenticationEnabled: boolean): void {
	if (!authenticationEnabled) return;
	hostWindow()?.postMessage({ type: AUTH_REQUEST_MESSAGE }, "*");
}

export function isAuthenticatedSession(): boolean {
	return authenticated;
}

/**
 * Empty when anonymous — callers send it only if non-empty.
 *
 * Also empty when the site has stopped vouching for this visitor (SD-13570
 * D6b). Withholding the key here rather than at each call site is deliberate:
 * every path that could answer personally reads the key from here, so one
 * check covers all of them and none can forget it.
 */
export function getSessionKey(): string {
	return confirmed ? sessionKey : "";
}
